import { getProductRoute } from "../product";
import { getPropertiesGroupedByName } from "../properties";
import type {
  AnyLineItemType,
  ExtendedLineItemType,
  VendorGroupType,
  VendorGroupByVendorIdType,
  PreparedLineItemType,
} from "../../types";
import type {
  LineItemTypeExtension,
  OrderLineItemTypeExtension,
  QuoteItemType,
  CartConfigurationItemTypeTl,
} from "@/core/api/graphql/types";
import type { CheckoutLineItemType } from "@/shared/checkout";

export function isQuoteItemType(item: AnyLineItemType): item is QuoteItemType {
  return "proposalPrices" in item || "selectedTierPrice" in item;
}

export function getTitle(item: LineItemTypeExtension | OrderLineItemTypeExtension | null) {
  if (item) {
    return item.product?.masterVariation
      ? item.product?.masterVariation?.slug
      : item.product?.isConfigurable
        ? item.name
        : item.sku;
  }

  return "";
}

/** @deprecated Use {@link groupByVendor} instead. */
export const getLineItemsGroupedByVendor = groupByVendor;

export function groupByVendor<T extends LineItemTypeExtension | OrderLineItemTypeExtension>(
  items: T[],
): VendorGroupType<T>[] {
  // NOTE: The group without the vendor should be displayed last.
  const groupWithoutVendor: VendorGroupType<T> = { items: [] };
  const map: VendorGroupByVendorIdType<T> = {};

  items.forEach((item) => {
    const vendor = item.vendor;

    if (vendor) {
      const vendorId = vendor.id;

      map[vendorId] = map[vendorId] || { vendor, items: [] };
      map[vendorId].items.push(item);
    } else {
      groupWithoutVendor.items.push(item);
    }
  });

  const result = Object.values(map)
    // Sort by Vendor
    .sort((a, b) => a.vendor!.name.localeCompare(b.vendor!.name));

  // Add the group without the vendor to the end.
  result.push(groupWithoutVendor);

  return result;
}

/** @deprecated Use {@link prepareLineItem} function */
export function extendLineItem<T extends AnyLineItemType>(item: T): ExtendedLineItemType<T> {
  return {
    ...item,
    extended: {
      isProductExists: !!item.product,
      route: getProductRoute(item.productId || item.product?.id || "", item.product?.slug),
      displayProperties: Object.values(getPropertiesGroupedByName(item.product?.properties ?? [])).slice(0, 3),
      minQuantity: item.product?.minQuantity,
      maxQuantity:
        item.product?.maxQuantity ??
        (<LineItemTypeExtension>item).inStockQuantity ??
        item.product?.availabilityData?.availableQuantity,
    },
  };
}

function prepareItemPrices(item: AnyLineItemType) {
  const price = "price" in item ? item.price : undefined;
  const listPrice = price ?? ("listPrice" in item ? item.listPrice : undefined);
  const salePrice = "salePrice" in item ? item.salePrice : undefined;
  const placedPrice = "placedPrice" in item ? item.placedPrice : undefined;
  const extendedPrice = "extendedPrice" in item ? item.extendedPrice : undefined;
  return {
    listPrice,
    salePrice,
    placedPrice,
    extendedPrice,
    actualPrice: placedPrice ?? salePrice ?? listPrice,
  };
}

export function prepareLineItem(item: AnyLineItemType, countInCart?: number): PreparedLineItemType {
  const { listPrice, extendedPrice, actualPrice } = prepareItemPrices(item);

  const productType = "productType" in item ? item.productType : undefined;
  const isVariation = !!item.product?.masterVariation;
  const quantity = isQuoteItemType(item) ? item.selectedTierPrice?.quantity : item.quantity;
  const inStockQuantity =
    "inStockQuantity" in item ? item.inStockQuantity : item.product?.availabilityData?.availableQuantity;
  const properties = Object.values(getPropertiesGroupedByName(item.product?.properties ?? []));
  const route = isVariation
    ? getProductRoute(item.product!.masterVariation!.id || "", item.product!.masterVariation!.slug)
    : getProductRoute(item.productId || item.product?.id || "", item.product?.slug);

  return {
    id: item.id,
    name: item.name || "",
    imageUrl: item.imageUrl,
    availabilityData: item.product?.availabilityData,
    productType,
    sku: item.sku,
    productId: item.productId,
    listPrice,
    actualPrice,
    extendedPrice,
    quantity,
    inStockQuantity,
    route,
    deleted: !item.product,
    properties: properties.slice(0, 3),
    countInCart,
    minQuantity: item.product?.minQuantity,
    maxQuantity:
      item.product?.maxQuantity ??
      (<LineItemTypeExtension>item).inStockQuantity ??
      item.product?.availabilityData?.availableQuantity,
  };
}

export function prepareLineItems(items: AnyLineItemType[]): PreparedLineItemType[] {
  return items.map((item) => prepareLineItem(item));
}

const allowedEnclosuresProductCodes = [
  "EC1515A-CUSTOM",
  "EC1530A-CUSTOM",
  "EC1530B-CUSTOM",
  "EC2030A-CUSTOM",
  "EC2030B-CUSTOM",
  "EC2530C-CUSTOM",
  "EC1515AR-CUSTOM",
  "EC1530AR-CUSTOM",
  "EC1530BR-CUSTOM",
  "EC2030AR-CUSTOM",
  "EC2030BR-CUSTOM",
  "EC2530CR-CUSTOM",
];

export function shouldShowComments(item: LineItemTypeExtension): boolean {
  const productCode = item.product?.code || "";
  const hasMatchingProductCode = allowedEnclosuresProductCodes.includes(productCode);
  const hasCustomText = item.configurationItemsTL?.some((c: CartConfigurationItemTypeTl) =>
    Boolean(c.customText && c.customText !== ""),
  );
  return hasMatchingProductCode && Boolean(hasCustomText);
}

export function shouldShowCommentsForOrder(item: OrderLineItemTypeExtension | CheckoutLineItemType): boolean {
  const productCode = item.product?.code || "";
  const hasMatchingProductCode = allowedEnclosuresProductCodes.includes(productCode);
  const hasCustomText = item.configurationItems?.some((c: CartConfigurationItemTypeTl) =>
    Boolean(c.customText && c.customText !== ""),
  );
  return hasMatchingProductCode && Boolean(hasCustomText);
}
